import { batch, createEffect, createMemo, createSignal, on, onCleanup, untrack } from "solid-js";
import { ActionType } from "../pb/mj/record/v1/record_pb";
import { PromptKind } from "../pb/mj/protocol/v1/protocol_pb";
import type { Action } from "../pb/mj/record/v1/record_pb";
import { GameSocket } from "../net/socket";
import type { Status } from "../net/socket";
import type { ServerMessage } from "../pb/mj/protocol/v1/protocol_pb";
import { autoAction, DEFAULT_SETTINGS, RIICHI_DISCARD_MS, riichiDiscard } from "./auto";
import { lowTimeMarks } from "./clock";
import { PULSE, pulse, pulseFor } from "./haptics";
import { arrange, callSlotsOf, move, slotsOf } from "./arrange";
import { play, soundsFor } from "./sound";
import type { PlaySettings } from "./auto";
import { NO_TILE } from "./tiles";
import { apply, emptyView, markSent, withOwnDiscard } from "./view";
import type { View } from "./view";

// One game connection per signed-in tab, open on every page (design 7 §6.1): it is what presence counts,
// and a table started by its creator reaches every seated player at once.

export interface GameSession {
  view: () => View;
  status: () => Status;
  act(a: Action): void;
  reading(handIndex: number, done: boolean): void; // the hand's result shown, then closed (the next hand waits)
  hand: () => number[]; // the own concealed tiles as shown: sorted, or as the player arranged them
  rearrange(tile: number, to: number): void; // with auto-sort off: move a tile to another slot
  resume(): void; // take the game back after another tab took it (status "superseded")
  setAids(on: boolean): void; // whether the unranked aids may answer: the table's page says
  press(type: ActionType): void; // a button with nothing behind it, at a table without assists (design 14 §6.6)
}

export function createGameSession(enabled: () => boolean, onGame: (gameId: string) => void, settings: () => PlaySettings = () => DEFAULT_SETTINGS): GameSession {
  const [served, setView] = createSignal<View>(emptyView());
  // What the player sees: the server's view, with their own discard made as they press it.
  const [pressed, setPressed] = createSignal<{ promptId: number; tile: number } | null>(null);
  const view = createMemo(() => {
    const v = served();
    const d = pressed();
    return d ? withOwnDiscard(v, d.promptId, d.tile) : v;
  });
  const [status, setStatus] = createSignal<Status>("connecting");
  let socket: GameSocket | null = null;

  // On or off, not every change of the account: a new settings object must not drop the connection.
  const live = createMemo(enabled);
  let lastGame = "";
  createEffect(() => {
    if (!live()) return;
    // Messages that arrive within one frame are applied in one update (design 10 §3): a discard and the
    // next draw are drawn once, not twice. A hidden page has no frames, so a timer stands in.
    let queue: { m: ServerMessage; at: number }[] = [];
    let pending = false;
    const flush = () => {
      if (!pending) return;
      pending = false;
      const batch = queue;
      queue = [];
      setView((v) => batch.reduce((acc, x) => apply(acc, x.m, x.at), v));
    };
    const s = new GameSocket({
      message: (m) => {
        queue.push({ m, at: performance.now() });
        if (!pending) {
          pending = true;
          if (document.hidden) setTimeout(flush, 0);
          else {
            requestAnimationFrame(flush);
            setTimeout(flush, 100); // should the page be hidden before the frame
          }
        }
        const b = m.body;
        // A game starting takes the tab to its table. Coming back to one already running does not: a player who went
        // elsewhere, or reloaded elsewhere, stays there, and Play offers the way back.
        if (b.case === "resync") lastGame = b.value.table?.gameId ?? lastGame;
        if (b.case === "tableAssigned" && b.value.gameId !== lastGame) {
          lastGame = b.value.gameId;
          onGame(lastGame);
        }
      },
      status: setStatus,
    });
    socket = s;
    s.start();
    onCleanup(() => {
      s.stop();
      socket = null;
    });
  });

  // The own hand's order (design 8 §2): sorted with auto-sort on, else the player's, kept through the
  // hand: each tile that came in since keeps its place at the right.
  const [kept, setKept] = createSignal<{ hand: number; tiles: number[] }>({ hand: -1, tiles: [] });
  const hand = createMemo(() => {
    const v = view();
    const concealed = v.seats[v.seat]?.concealed;
    if (!concealed) return [];
    const handIndex = v.hand?.index ?? -1;
    const k = kept();
    return settings().auto_sort ? concealed : arrange(k.hand === handIndex ? k.tiles : [], concealed);
  });
  createEffect(() => {
    const h = hand();
    const handIndex = view().hand?.index ?? -1;
    const k = untrack(kept);
    if (k.hand !== handIndex || k.tiles.length !== h.length || k.tiles.some((t, i) => t !== h[i])) setKept({ hand: handIndex, tiles: h });
  });
  const rearrange = (tile: number, to: number) => setKept((k) => ({ ...k, tiles: move(k.tiles, tile, to) }));

  // The tile of a riichi declared with its tile chosen (ui/Table): it goes once the server asks for it.
  let riichiTile: number | null = null;
  // The answer goes out once, from the latest view.
  const act = (asked: Action) => {
    if (untrack(status) !== "open") return; // it would go nowhere, and look accepted: the tile flew, then came home with others in the river (UX review 2026-11-02)
    const v = untrack(served);
    const p = v.prompt;
    if (!p || p.sent) return;
    const id = p.prompt.promptId;
    let a = asked;
    if (a.type === ActionType.DECLARE_RIICHI && a.tiles.length === 1) {
      riichiTile = a.tiles[0]!;
      a = p.prompt.legal.find((x) => x.type === ActionType.DECLARE_RIICHI) ?? a;
    }
    const me = v.seats[v.seat];
    const shown = untrack(hand);
    const sorted = untrack(settings).auto_sort;
    const slots = a.type === ActionType.DISCARD && me ? slotsOf(shown, me.drawn, a.tiles[0] ?? NO_TILE, sorted) : undefined;
    const calls = [ActionType.CHI, ActionType.PON, ActionType.OPEN_KAN, ActionType.CLOSED_KAN, ActionType.ADDED_KAN];
    const callSlots = calls.includes(a.type) && me ? callSlotsOf(shown, me.drawn, a.tiles) : [];
    socket?.act(v.gameId, id, a, slots, callSlots);
    batch(() => {
      setView((cur) => markSent(cur, id)); // the prompt answered, not whichever is open when this runs
      // A riichi's tile turns sideways and a stick comes with it: those wait to be told. A plain discard has nothing to wait for.
      if (a.type === ActionType.DISCARD && p.prompt.kind !== PromptKind.RIICHI_DISCARD) setPressed({ promptId: id, tile: a.tiles[0] ?? NO_TILE });
    });
  };

  createEffect(() => {
    const p = served().prompt;
    if (riichiTile === null || !p || p.sent || p.prompt.kind !== PromptKind.RIICHI_DISCARD) return;
    const d = p.prompt.legal.find((x) => x.type === ActionType.DISCARD && x.tiles[0] === riichiTile);
    riichiTile = null;
    if (d) untrack(() => act(d));
  });

  const [aids, setAids] = createSignal(false);
  const press = (type: ActionType) => socket?.press(untrack(served).gameId, type, untrack(served).lastSeq);
  const resume = () => socket?.start();
  const reading = (handIndex: number, done: boolean) => socket?.reading(untrack(served).gameId, handIndex, done);

  // Sound, when the player turned it on (design 8 item 6), and the pulses (design 10 §8): what each update calls for, once.
  createEffect(
    on(served, (v, prev) => {
      const s = untrack(settings);
      if (!prev) return;
      if (s.sound) for (const x of soundsFor(prev, v, !untrack(aids))) play(x, s.volume);
      const felt = s.haptics ? pulseFor(prev, v) : null;
      if (felt !== null) pulse(felt);
    }),
  );
  // Turned on, it says so: a player who cannot hear it finds out now, not at the table. And it is a tap, which is what lets it play.
  const soundOn = createMemo(() => settings().sound);
  createEffect(
    on(
      soundOn,
      (on_) => {
        if (on_) play("turn", untrack(settings).volume);
      },
      { defer: true },
    ),
  );

  // The last seconds of the seat's own clock: a tick each, with the sound on, and a pulse as they begin (design 10 §8).
  const clockPrompt = createMemo(() => {
    const p = served().prompt;
    return p && !p.sent ? p : null;
  });
  createEffect(() => {
    const p = clockPrompt();
    if (!p) return;
    const timers = lowTimeMarks(p.prompt, p.receivedAt, performance.now()).map((m, i) =>
      setTimeout(() => {
        const s = untrack(settings);
        if (s.sound) play("low", s.volume);
        if (i === 0 && s.haptics) pulse(PULSE.call);
      }, m),
    );
    onCleanup(() => timers.forEach(clearTimeout));
  });

  // The unranked aids answer a new prompt themselves when the player turned them on (design 8 P3), at the table that
  // allows them: the page says.
  createEffect(() => {
    if (!aids()) return;
    const a = autoAction(view(), settings());
    if (a) untrack(() => act(a));
  });
  // In riichi the hand cannot change: a drawn tile that offers nothing goes by itself, a moment after it is seen. Not at a table without assists,
  // where going by itself would say that nothing was on offer.
  createEffect(() => {
    if (!aids()) return;
    const a = riichiDiscard(served());
    if (!a) return;
    const id = setTimeout(() => untrack(() => act(a)), RIICHI_DISCARD_MS);
    onCleanup(() => clearTimeout(id));
  });

  return { view, status, act, reading, resume, hand, rearrange, setAids, press };
}
